import Autocomplete from '@betterfunnel/ui-core/Autocomplete';
import Button from '@betterfunnel/ui-core/Button';
import IconButton from '@betterfunnel/ui-core/IconButton';
import Modal, { ModalActionData } from '@betterfunnel/ui-core/Modal';
import TextField from '@betterfunnel/ui-core/TextField';
import { DynamicPage } from '@betterfunnel/types';
import { DialogTitle } from '@headlessui/react';
import { EyeIcon, EyeSlashIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import { diff } from 'deep-diff';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { uid } from 'uid';
import { useAuditLog } from '../../contexts/AuditLogContext';
import { useSnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import {
    getAllDynamicPagesLinks,
    replaceAllDynamicPagesLinks,
} from '../../services/DynamicPagesService';
import { distinct } from '../../utils';
import {
    formFieldPhoneNumberValidator,
    formFieldUrlValidator,
    mergeFromFieldValidators,
} from '../../utils/validator';

interface Props {
  dynamicPages: QueryDocumentSnapshot<DynamicPage>[] | string[];
  open: boolean;
  onClose: () => void;
}

export default function UpdateDynamicPagesLinksModal({
  dynamicPages: _dynamicPagesToEdit,
  open,
  onClose,
}: Props) {
  const { show } = useSnackbarContext();
  const { selectedAccount } = useContext(UserContext);
  const auditLog = useAuditLog();
  const [linkOptions, setLinkOptions] = useState<string[]>([]);
  const [fetchingLinkOptions, setFetchingLinkOptions] = useState(false);
  const [errorFetchingLinkOptions, setErrorFetchingLinkOptions] =
    useState<Error | null>(null);

  const [mappings, setMappings] = useState<
    { link: string; toLink: string | null; id: string; expanded?: boolean }[]
  >([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    if (!open) return;

    setMappings([
      {
        link: '',
        toLink: null,
        id: uid(8),
      },
    ]);
    setSaving(false);
    setError(undefined);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    setLinkOptions([]);
    setFetchingLinkOptions(true);
    setErrorFetchingLinkOptions(null);

    if (!selectedAccount) return;

    let relevant = true;
    getAllDynamicPagesLinks(
      selectedAccount?.id,
      _dynamicPagesToEdit.map((d) => (typeof d === 'string' ? d : d.id)),
    )
      .then((links) => {
        if (!relevant) return;
        setLinkOptions(links);
        setFetchingLinkOptions(false);
      })
      .catch((e) => {
        if (!relevant) return;
        setErrorFetchingLinkOptions(e);
        setFetchingLinkOptions(false);
        Sentry.captureException(e);
        console.error(e);
      });

    return () => {
      relevant = false;
    };
  }, [open, selectedAccount, _dynamicPagesToEdit]);

  const updateLinks = useCallback(async () => {
    if (saving) return;
    if (!selectedAccount) return;

    if (!mappings.length) {
      setError(new Error('No link changes selected'));
      return;
    }
    if (mappings.some((mapping) => !mapping.link)) {
      setError(new Error('Please select a link to change'));
      return;
    }
    if (mappings.some((mapping) => !mapping.toLink)) {
      setError(new Error('Please select a new link'));
      return;
    }
    if (
      distinct(mappings.map((mapping) => mapping.link)).length !==
      mappings.length
    ) {
      setError(new Error('Please select unique links to change'));
      return;
    }

    setSaving(true);

    try {
      auditLog.log(
        'update',
        'Bulk update dynamic pages links',
        'dynamicPages',
        undefined,
        undefined,
        diff({}, { mappings }),
      );

      const linksToUpdate = mappings.map((mapping) => ({
        link: mapping.link,
        toLink: mapping.toLink || '',
      }));
      await replaceAllDynamicPagesLinks(
        selectedAccount.id,
        _dynamicPagesToEdit.map((d) => (typeof d === 'string' ? d : d.id)),
        linksToUpdate,
      );

      onClose();

      show('Updated links, requires publish to take effect', 'success');
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setSaving(false);
  }, [
    _dynamicPagesToEdit,
    auditLog,
    mappings,
    onClose,
    saving,
    selectedAccount,
    show,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: saving ? 'Updating, might take some time' : 'Update',
        type: 'primary',
        disabled: saving,
        onClick: () => updateLinks(),
      },
    ],
    [saving, onClose, updateLinks],
  );

  return (
    <Modal open={open} onClose={onClose} actions={actions} width="5xl">
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Update Dynamic Pages Links
      </DialogTitle>
      <div className="relative mt-4 flex min-h-48 w-full flex-col gap-4">
        {errorFetchingLinkOptions ? (
          <div className="w-full text-end text-red-600">
            {errorFetchingLinkOptions.message || 'An error occurred'}
          </div>
        ) : (
          <></>
        )}
        {mappings.map((mapping) => (
          <div
            className="relative flex w-full flex-col items-stretch gap-4 rounded-lg border bg-gray-50 p-4"
            key={mapping.id}
          >
            <div className="absolute right-0 top-0 flex w-full items-center justify-end p-2">
              <IconButton
                variant="ternary"
                onClick={() => {
                  setMappings((prev) =>
                    prev.map((m) => {
                      if (m.id === mapping.id) {
                        return { ...m, expanded: !mapping.expanded };
                      }
                      return m;
                    }),
                  );
                }}
              >
                {mapping.expanded ? (
                  <EyeSlashIcon className="size-4 text-gray-500" />
                ) : (
                  <EyeIcon className="size-4" />
                )}
              </IconButton>
              <IconButton
                color="negative"
                variant="ternary"
                disabled={mappings.length <= 1}
                onClick={() => {
                  setMappings((prev) =>
                    prev.filter((m) => m.id !== mapping.id),
                  );
                }}
              >
                <TrashIcon className="size-4" />
              </IconButton>
            </div>
            <div className="flex-1 shrink-0">
              <Autocomplete<string>
                title="From:"
                selected={mapping.link}
                onChange={(s) => {
                  setMappings((prev) =>
                    prev.map((m) => {
                      if (m.id === mapping.id) {
                        return { ...m, link: s || '' };
                      }
                      return m;
                    }),
                  );
                }}
                placeholder="Select a link"
                options={linkOptions.filter((l) => {
                  return !mappings.some((mapping) => mapping.link === l);
                })}
                loading={fetchingLinkOptions}
                disabled={fetchingLinkOptions}
              />
              {mapping.expanded && (
                <span className="break-all text-xs text-gray-500">
                  {mapping.link}
                </span>
              )}
            </div>
            <div className="flex-1 shrink-0">
              <TextField
                title="To:"
                value={mapping.toLink || ''}
                onChange={(e) => {
                  setMappings((prev) =>
                    prev.map((m) => {
                      if (m.id === mapping.id) {
                        return { ...m, toLink: e.target.value };
                      }
                      return m;
                    }),
                  );
                }}
                placeholder="New link"
                disabled={fetchingLinkOptions || !linkOptions.length}
                validation={
                  typeof mapping.toLink === 'string'
                    ? mergeFromFieldValidators(
                        'atLeastOnePassing',
                        formFieldUrlValidator(mapping.toLink, true),
                        formFieldPhoneNumberValidator(mapping.toLink, true),
                      )
                    : undefined
                }
              />
              {mapping.expanded && (
                <span className="break-all text-xs text-gray-500">
                  {mapping.toLink}
                </span>
              )}
            </div>
          </div>
        ))}
        <Button
          icon={<PlusIcon className="h-5 w-5" />}
          variant="ternary"
          className="self-start"
          onClick={() => {
            setMappings((prev) => [
              ...prev,
              {
                link: '',
                toLink: '',
                id: uid(8),
              },
            ]);
          }}
        >
          Add another link pair
        </Button>
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'An error occurred'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
